import { useImperativeHandle, useRef } from "react";

import {
  FieldDefinitionFieldType,
  FieldDefinitionListType,
  FieldImperativeHandle,
} from "@/components/@types";
import { Utils } from "@/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { typographyStyles } from "@/components/ui/typography";

function FieldListValue<T extends FieldDefinitionListType<T>>({
  value,
  isMultiline = false,
  ref,
}: FieldDefinitionFieldType<FieldDefinitionListType<T>> & {
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const firstButtonRef = useRef<HTMLButtonElement>(null);

  const textStyle = isMultiline
    ? ({
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      } as const)
    : ({
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      } as const);

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        firstButtonRef.current?.focus();
      },
      unsetFocus: () => {
        firstButtonRef.current?.blur();
      },
    }),
    [],
  );

  return (
    <div
      style={{
        padding: 0,
        maxWidth: "100%",
      }}
    >
      {value?.map((item, index) => (
        <div
          key={item.id}
          style={{
            padding: `${"8px"} 0`,
            borderRadius: "calc(var(--radius) - 2px)",
          }}
        >
          <button
            ref={index === 0 ? firstButtonRef : undefined}
            onClick={item.onClick}
            data-testid={`field-list-item-${item.title}`}
            style={{
              width: "100%",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: 0,
              textAlign: "left",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: isMultiline ? "flex-start" : "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  minWidth: 0,
                  flex: 1,
                }}
              >
                <div
                  style={{
                    ...(typographyStyles["body-md"] as React.CSSProperties),
                    ...textStyle,
                  }}
                >
                  {item.title}
                </div>
                <div
                  style={{
                    ...(typographyStyles["body-sm"] as React.CSSProperties),
                    color:
                      "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
                    ...textStyle,
                  }}
                >
                  {item.description}
                </div>
              </div>
              <div style={{ flexShrink: 0, marginLeft: "8px" }}>
                <div
                  style={{
                    ...(typographyStyles["body-sm"] as React.CSSProperties),
                    color:
                      "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {Utils.fTimeAgo(item.createdAt)}
                </div>
              </div>
            </div>
          </button>
        </div>
      ))}
    </div>
  );
}

function FieldListSkeleton() {
  return (
    <div style={{ padding: 0, maxWidth: "100%" }}>
      {[1, 2, 3].map((index) => (
        <div key={index} style={{ padding: `${"8px"} 0` }}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                flex: 1,
              }}
            >
              <Skeleton
                variant="text"
                level="body-md"
                style={{
                  width: "80%",
                  height: 20,
                }}
              />
              <Skeleton
                variant="text"
                level="body-sm"
                style={{
                  width: "60%",
                  height: 16,
                }}
              />
            </div>
            <div style={{ marginLeft: "8px" }}>
              <Skeleton
                variant="text"
                level="body-sm"
                style={{
                  width: 60,
                  height: 16,
                }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function FieldList<T extends FieldDefinitionListType<T>>({
  isLoading,
  ref,
  ...props
}: FieldDefinitionFieldType<FieldDefinitionListType<T>> & {
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  if (isLoading) {
    return <FieldListSkeleton />;
  }

  return <FieldListValue {...props} ref={ref} />;
}
